// useGlobalSearch.ts — debounced, cancellable, order-safe fetch of the
// global-search backend for the command palette's async result groups.
//
// Why a hand-rolled hook instead of TanStack Query (the house default):
// TanStack Query's cancellation is reliable for (a) unmount and
// (b) queryClient.cancelQueries(), but switching to a NEW queryKey while the
// PREVIOUS key's fetch is still in flight does not guarantee that fetch is
// aborted, and gives no built-in guarantee about which of two in-flight
// fetches "wins" if they resolve out of order. This hook has a hard
// requirement — never render a stale keystroke's response over a newer
// one, even if the stale one resolves later over a slow connection — so it
// uses a local AbortController + monotonic sequence ref instead. This
// mirrors the existing house pattern for cancellable async fetches elsewhere
// in the app (useTourRecommendation.ts, useNLBuilder.ts, useChatStream.ts,
// stategraphs/TestsTab.tsx all hand-roll AbortController for the same
// reason), so it is not a new idiom for this codebase.

import { useEffect, useRef, useState } from 'react'
import { globalSearchApi, type GlobalSearchGroup } from './globalSearchApi'

/** Debounce window between the last keystroke and firing the request. */
const DEBOUNCE_MS = 150
/** Grace period before the loading spinner is allowed to appear. */
const SPINNER_DELAY_MS = 100
/** Below this length the query is not sent at all. */
const MIN_QUERY_LEN = 2

export interface UseGlobalSearchResult {
  /** Server-returned groups for the most recent settled query. Empty when
   *  disabled, cleared, or before the first response arrives. */
  groups: GlobalSearchGroup[]
  /** True only once a request has been in flight for >= SPINNER_DELAY_MS.
   *  Fast responses never flip this — avoids the flicker of a spinner that
   *  appears and disappears within a single frame. */
  showSpinner: boolean
}

/**
 * Debounced (150ms) fetch of GET /tenant/search/global for the palette.
 *
 * - Disabled below MIN_QUERY_LEN characters (no request fired).
 * - Clears `groups` synchronously (no debounce) when `query` becomes empty.
 * - Aborts the previous in-flight request whenever a new one is about to
 *   start.
 * - Monotonic sequence guard: a response is only applied if it belongs to
 *   the most recently ISSUED request, regardless of resolution order — so a
 *   slow older request that resolves after a newer one is silently dropped.
 * - A 429 (rate limit) or any other request failure keeps the previous
 *   `groups` untouched; there is no per-keystroke error surfaced to the UI.
 */
export function useGlobalSearch(query: string, allHistory: boolean): UseGlobalSearchResult {
  const [groups, setGroups] = useState<GlobalSearchGroup[]>([])
  const [showSpinner, setShowSpinner] = useState(false)

  // Bumped on every request we issue (or invalidate); a response is only
  // applied when its captured sequence number still matches the ref, i.e.
  // no newer request has since been issued.
  const seqRef = useRef(0)
  const abortRef = useRef<AbortController | null>(null)
  const spinnerTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearSpinnerTimer = () => {
    if (spinnerTimerRef.current !== null) {
      clearTimeout(spinnerTimerRef.current)
      spinnerTimerRef.current = null
    }
  }

  useEffect(() => {
    // Empty input: clear instantly, no debounce, and invalidate anything
    // still in flight so it can't repopulate `groups` after the fact.
    if (query.length === 0) {
      seqRef.current += 1
      abortRef.current?.abort()
      clearSpinnerTimer()
      setShowSpinner(false)
      setGroups([])
      return
    }

    // Below the minimum length: disabled — same invalidation as empty.
    if (query.trim().length < MIN_QUERY_LEN) {
      seqRef.current += 1
      abortRef.current?.abort()
      clearSpinnerTimer()
      setShowSpinner(false)
      setGroups([])
      return
    }

    const debounceTimer = setTimeout(() => {
      // A prior request (from an earlier keystroke that survived the
      // debounce, e.g. slow network) may still be in flight — cancel it
      // before starting the new one.
      abortRef.current?.abort()
      const controller = new AbortController()
      abortRef.current = controller

      const mySeq = ++seqRef.current

      clearSpinnerTimer()
      spinnerTimerRef.current = setTimeout(() => {
        if (mySeq === seqRef.current) setShowSpinner(true)
      }, SPINNER_DELAY_MS)

      globalSearchApi
        .search(query, { allHistory, signal: controller.signal })
        .then((res) => {
          // Superseded by a newer request issued after this one started —
          // drop it even though it resolved successfully.
          if (mySeq !== seqRef.current) return
          setGroups(res.groups)
        })
        .catch((err) => {
          // AbortError is expected (we caused it); anything else (429,
          // network failure, 5xx) is deliberately swallowed here — the
          // caller keeps showing whatever `groups` already holds rather
          // than surfacing an error per keystroke.
          if (err instanceof Error && err.name === 'AbortError') return
        })
        .finally(() => {
          if (mySeq === seqRef.current) {
            clearSpinnerTimer()
            setShowSpinner(false)
          }
        })
    }, DEBOUNCE_MS)

    return () => clearTimeout(debounceTimer)
  }, [query, allHistory])

  // Unmount cleanup — abort whatever is still in flight. Refs are stable
  // across renders, so an empty dependency array is correct here (not a
  // missing-deps lint violation).
  useEffect(() => {
    return () => {
      abortRef.current?.abort()
      clearSpinnerTimer()
    }
  }, [])

  return { groups, showSpinner }
}
